// src/styles/AnalysisStyles.js
import styled from 'styled-components';
import { keyframes, css } from 'styled-components';

export const AnalysisContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

export const ContentContainer = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
`;

export const SummaryContainer = styled.div`
  display: flex;
  gap: 2rem;
  margin-bottom: 2rem;
`;

export const SummaryTableContainer = styled.div`
  flex: 1;
`;

export const IconSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
`;

export const IconContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-left: 2rem;
`;

export const ApkIcon = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

export const AnalysisSection = styled.div`
  background-color: #ffffff;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

export const HashDisplay = styled.div`
  font-family: monospace;
  padding: 1rem;
  background-color: #f3f4f6;
  border-radius: 4px;
  margin-bottom: 1rem;
  word-break: break-all;
`;

export const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

export const InfoCard = styled.div`
  padding: 1rem;
  background-color: #f8fafc;
  border-radius: 4px;
  border: 1px solid #e2e8f0;
`;

export const InfoTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 0.5rem;
`;

export const SummaryTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

export const ComponentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin-top: 2rem;
`;

export const ComponentBox = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: transform 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;

  &:hover {
    transform: translateY(-2px);
  }
`;

export const ComponentIcon = styled.div`
  width: 64px;
  height: 64px;
  background: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
`;

export const ComponentTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

export const ButtonsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
`;

export const ComponentButtonsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin: 2rem 0;
`;

export const ComponentButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.5rem;
  border: none;
  border-radius: 8px;
  background-color: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-2px);
    background-color: #f8fafc;
  }
`;

export const ComponentName = styled.span`
  font-size: 1.1rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.5rem;
`;

export const ComponentCount = styled.span`
  font-size: 2rem;
  font-weight: 700;
  color: #3b82f6;
`;

export const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

export const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 800px;
  max-height: 80vh;
  padding: 2rem;
  position: relative;
  display: flex;
  flex-direction: column;
`;

export const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
`;

export const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  padding: 0.5rem;

  &:hover {
    color: #1f2937;
  }
`;

export const ModalBody = styled.div`
  overflow-y: auto;
  flex-grow: 1;
`;

const pulseAnimation = keyframes`
  0%, 100% { 
    opacity: 1; 
  }
  50% { 
    opacity: 0.5; 
  }
`;

export const ListItem = styled.div`
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
  color: ${props => props.suspicious ? '#dc2626' : 'inherit'};

  &:last-child {
    border-bottom: none;
  }
`;

export const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 500;
  
  ${props => {
    switch (props.type) {
      case 'completed':
        return `
          background-color: #dcfce7;
          color: #166534;
        `;
      case 'analyzing':
        return `
          background-color: #dbeafe;
          color: #1e40af;
        `;
      case 'packed':
        return `
          background-color: #4982ceff;
          color: #ffffffff;
        `;
      case 'failed':
        return `
          background-color: #fee2e2;
          color: #991b1b;
        `;
      case 'malware':
        return `
          background-color: #fee2e2;
          color: #991b1b;
        `;
      case 'warning':
        return `
        background-color: #d94242ff;
          color: #ffffffff;
          `
      case 'frosted':
        return `
          background-color: #38e217ff;
          color: #ffffffff;
        `;
      case 'tag':
        return `
          background-color: #e0e7ff;
          color: #3730a3;
        `;

      default:
        return `
          background-color: #f3f4f6;
          color: #4b5563;
        `;
    }
  }}
`;

export const TableCell = styled.td`
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
`;

export const TableHeader = styled.th`
  padding: 1rem;
  text-align: left;
  background-color: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  width: 150px;
`;

export const TabContainer = styled.div`
  width: 100%;
  margin-top: 2rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  overflow: hidden;
`;

export const TabList = styled.div`
  display: flex;
  gap: 1rem;
  border-bottom: 2px solid #e5e7eb;
  margin-bottom: 2rem;
`;

export const TabButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  background: none;
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.active ? '#3b82f6' : '#6b7280'};
  border-bottom: 2px solid ${props => props.active ? '#3b82f6' : 'transparent'};
  margin-bottom: -2px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    color: #3b82f6;
  }
`;

export const TabContent = styled.div`
  display: ${props => props.active ? 'block' : 'none'};
`;

export const BookmarkButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: ${props => props.bookmarked ? '#3b82f6' : 'white'};
  color: ${props => props.bookmarked ? 'white' : '#3b82f6'};
  border: 2px solid #3b82f6;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: ${props => props.bookmarked ? '#2563eb' : '#f3f4f6'};
  }
`;

export const IconImage = styled.img`
  width: 128px;
  height: 128px;
  border-radius: 16px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 1rem;
`;

export const BookmarkIcon = styled.div`
  width: 24px;
  height: 24px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='${props => props.isBookmarked ? 'rgba(59, 130, 246, 1)' : 'none'}' stroke='${props => props.isBookmarked ? 'rgba(59, 130, 246, 1)' : 'currentColor'}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
`;

export const IntelligenceTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 2rem;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

export const IntelligenceTableHeader = styled.th`
  padding: 1rem;
  text-align: left;
  background-color: #f9fafb;
  border-bottom: 2px solid #e5e7eb;
`;

export const IntelligenceTableCell = styled.td`
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
  width: '40%';
  wordBreak: 'break-word';
  overflowWrap: 'break-word';
  maxWidth: '0';
`;

export const Tag = styled.span`
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  margin-right: 0.5rem;
  
  ${props => {
    switch (props.type) {
      case 'file':
        return 'background-color: #dcfce7; color: #166534;';
      case 'crypto':
        return 'background-color: #fee2e2; color: #991b1b;';
      case 'intent':
        return 'background-color: #dbeafe; color: #1e40af;';
      case 'accessibility':
        return 'background-color: #FFDEAD; color:	#D2691E';
      default:
        return 'background-color: #f3f4f6; color: #4b5563;';
    }
  }}
`;

export const MethodsTabList = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
`;

export const MethodsTabButton = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  background: none;
  color: ${props => props.active ? '#3b82f6' : '#6b7280'};
  border-bottom: 2px solid ${props => props.active ? '#3b82f6' : 'transparent'};
  cursor: pointer;
  
  &:hover {
    color: #3b82f6;
  }
`;

export const IntelligenceButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 4px;
  border: none;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  ${props => {
    if (props.type === 'virustotal') {
      return `
        background-color: #fee2e2;
        color: #991b1b;
        &:hover {
          background-color: #fecaca;
        }
      `;
    }
    if (props.type === 'playstore') {
      return `
        background-color: #dcfce7;
        color: #166534;
        &:hover {
          background-color: #bbf7d0;
        }
      `;
    }
    if (props.type === 'pivot') {
      return `
        background-color: #dbeafe;
        color: #1e40af;
        font-size: 0.75rem;
        padding: 0.25rem 0.6rem;
        &:hover {
          background-color: #bfdbfe;
        }
      `;
    }
  }}
`;

export const MaliciousCount = styled.span`
  color: #991b1b;
  font-weight: 600;
  margin-right: 1rem;
`;

export const SectionButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
`;

export const SectionButton = styled.button`
  padding: 1rem 2rem;
  border-radius: 8px;
  border: none;
  background-color: ${props => props.active ? '#3b82f6' : '#f3f4f6'};
  color: ${props => props.active ? 'white' : '#4b5563'};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: ${props => props.active ? '#2563eb' : '#e5e7eb'};
  }
`;

export const Section = styled.div`
  display: ${props => props.visible ? 'block' : 'none'};
  animation: fadeIn 0.3s ease-in;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

export const CodeButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 4px;
  border: none;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  ${props => {
    if (props.disabled) {
      return `
        background-color: #e5e7eb;
        color: #9ca3af;
        cursor: not-allowed;
      `;
    }
    return `
      background-color: #dcfce7;
      color: #166534;
      &:hover {
        background-color: #bbf7d0;
      }
    `;
  }}
`;

export const CodeModal = styled(Modal)`
  /* Inherits base Modal styles */
`;

export const CodeModalContent = styled(ModalContent)`
  width: 90%;
  max-width: 800px;
  max-height: 80vh;
`;

export const CodePreview = styled.pre`
  background-color: #f3f4f6;
  padding: 1rem;
  border-radius: 4px;
  overflow-x: auto;
  white-space: pre-wrap;
  font-family: monospace;
`;

export const SignatureBlock = styled.pre`
  background-color: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 8px;
  font-family: monospace;
  font-size: 0.9em;
  overflow-x: auto;
  margin: 0;
`;

export const RescanButton = styled.button`
  background: transparent;
  border: 1px solid #e5e7eb;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.2s;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: ${props => props.disabled ? 'transparent' : 'rgba(0, 0, 0, 0.05)'};
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
  }
`;

const spinAnimation = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

export const RefreshIcon = styled.div`
  width: 24px;
  height: 24px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M23 4v6h-6'%3E%3C/path%3E%3Cpath d='M1 20v-6h6'%3E%3C/path%3E%3Cpath d='M3.51 9a9 9 0 0 1 14.85-3.36L23 10'%3E%3C/path%3E%3Cpath d='M20.49 15a9 9 0 0 1-14.85 3.36L1 14'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  
  ${props => props.isSpinning ? css`animation: ${spinAnimation} 1s linear infinite;` : ''}
`;

export const DownloadButton = styled.button`
  background: transparent;
  border: 1px solid #e5e7eb;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.2s;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: ${props => props.disabled ? 'transparent' : 'rgba(0, 0, 0, 0.05)'};
    transform: ${props => props.disabled ? 'none' : 'translateY(-1px)'};
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
  }
  
  &:active {
    transform: ${props => props.disabled ? 'none' : 'translateY(0)'};
  }
`;

export const DownloadIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  ${props => props.isDownloading ? css`
    animation: ${pulseAnimation} 1.5s ease-in-out infinite;
  ` : ''}
  
  /* Download arrow SVG when ready */
  ${props => !props.isDownloading && css`
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4'%3E%3C/path%3E%3Cpolyline points='7,10 12,15 17,10'%3E%3C/polyline%3E%3Cline x1='12' y1='15' x2='12' y2='3'%3E%3C/line%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: center;
    background-size: 16px 16px;
  `}
  
  /* Loading spinner SVG when downloading */
  ${props => props.isDownloading && css`
    &::before {
      content: '';
      width: 16px;
      height: 16px;
      border: 2px solid currentColor;
      border-radius: 50%;
      border-top-color: transparent;
      animation: ${spinAnimation} 1s linear infinite;
    }
  `}
`;

// Enhanced responsive table for better layout control
export const ResponsiveIntelligenceTable = styled(IntelligenceTable)`
  table-layout: fixed;
  width: 100%;
  min-width: 600px;
  overflow-x: auto;
`;

// Enhanced TabContent for better table layout
export const EnhancedTabContent = styled(TabContent)`
  overflow-x: auto;
  padding: 1rem;
`;

// Signature table specific styled components
export const SignatureTableHeader = styled(IntelligenceTableHeader)`
  &.filename-header {
    width: 40%;
  }
  
  &.reason-header {
    width: 35%;
  }
  
  &.signature-header {
    width: 25%;
  }
`;

export const SignatureTableCell = styled(IntelligenceTableCell)`
  &.filename-cell {
    width: 40%;
    word-break: break-all;
    overflow-wrap: break-word;
    max-width: 0;
    white-space: normal;
  }
  
  &.reason-cell {
    width: 35%;
    word-break: break-word;
    overflow-wrap: break-word;
    max-width: 0;
    white-space: normal;
  }
  
  &.signature-cell {
    width: 25%;
    word-break: break-all;
    overflow-wrap: break-word;
    max-width: 0;
    white-space: normal;
  }
`;

// New components for Base64 table improvements
export const Base64TableCell = styled(IntelligenceTableCell)`
  max-width: 0;
  word-break: break-all;
  overflow-wrap: break-word;
  white-space: normal;
  vertical-align: top;
  position: relative;
`;

export const TruncatedText = styled.pre`
  margin: 0;
  font-family: monospace;
  font-size: 0.875rem;
  white-space: pre-wrap;
  word-break: break-all;
  overflow-wrap: break-word;
  background-color: #f8f9fa;
  padding: 0.5rem;
  border-radius: 4px;
  border: 1px solid #e9ecef;
  max-height: 200px;
  overflow-y: auto;
  line-height: 1.4;
  
  /* Custom scrollbar for better UX */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

export const ExpandButton = styled.button`
  margin-top: 0.5rem;
  padding: 0.25rem 0.5rem;
  background-color: #e3f2fd;
  color: #1565c0;
  border: 1px solid #bbdefb;
  border-radius: 4px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #bbdefb;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const ReportButton = styled.button`
  background: transparent;
  border: 1px solid #e5e7eb;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.2s;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const ReportIcon = styled.div`
  width: 24px;
  height: 24px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z'%3E%3C/path%3E%3Cpolyline points='14 2 14 8 20 8'%3E%3C/polyline%3E%3Cline x1='16' y1='13' x2='8' y2='13'%3E%3C/line%3E%3Cline x1='16' y1='17' x2='8' y2='17'%3E%3C/line%3E%3Cpolyline points='10 9 9 9 8 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: 16px 16px;
`;

export const InvestigateButton = styled.button`
  background: transparent;
  border: 1px solid #e5e7eb;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.2s;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: rgba(99, 102, 241, 0.08);
    border-color: #818cf8;
    transform: translateY(-1px);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const InvestigateIcon = styled.div`
  width: 24px;
  height: 24px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%236366f1' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='11' cy='11' r='8'%3E%3C/circle%3E%3Cpath d='m21 21-4.35-4.35'%3E%3C/path%3E%3Ccircle cx='11' cy='11' r='3'%3E%3C/circle%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: 16px 16px;
`;

export const StatusWrapper = styled.span`
  position: relative;
  display: inline-block;
`;
